import { IconInfoCircle } from "@tabler/icons-react"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { validateWebhookUrl } from "lopermut"
import { FlaskConical, PlusIcon, XIcon } from "lucide-react"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { STATUSES_FOR_SHEET_ENABLED } from "modules/campaigns/lib/campaignStatus"
import { useTestWebhookMutation } from "modules/settings/hooks/zendesk/useTestWebhookMutation"
import { VaultVariableAutocomplete } from "modules/shared/components/VaultVariableAutocomplete"
import { ControllerRenderProps, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  CAMPAIGN_API_CALLBACK_DEFAULT_PAYLOAD,
  CAMPAIGN_API_CALLBACK_SAMPLE_DATA,
  CAMPAIGN_CALLBACK_URL_VARIABLES,
  CampaignCallbackHeadersSchema,
  CampaignDetailResponseDataSchema,
} from "schema"
import { EditApiCallbackPayloadDialog } from "./EditApiCallbackPayloadDialog"
import { toast } from "sonner"
import { z } from "zod"

type FormValues = z.infer<typeof UpdateCampaignSchemaFrontend>

const CALLBACK_URL_VARIABLE_OPTIONS = CAMPAIGN_CALLBACK_URL_VARIABLES.map((path) => ({
  value: path,
  label: path,
}))

type Props = {
  campaign: z.infer<typeof CampaignDetailResponseDataSchema>
  isDisabled: boolean
}

function normalizeHeaderRows(
  raw: z.infer<typeof CampaignCallbackHeadersSchema> | undefined,
): { key: string; value: string }[] {
  if (raw == null || !Array.isArray(raw)) return []
  return raw.map((h) => ({ key: h.key, value: h.value }))
}

export function ApiChannelForm({ campaign, isDisabled }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<FormValues>()
  const canEdit = STATUSES_FOR_SHEET_ENABLED.includes(campaign.status) && !isDisabled
  const { mutateAsync: testWebhook, isPending: isTestWebhookPending } = useTestWebhookMutation()

  const headers = normalizeHeaderRows(form.watch("callbackHeaders"))

  function patchCallbackHeaders(next: { key: string; value: string }[]) {
    form.setValue("callbackHeaders", next.length === 0 ? null : next, { shouldDirty: true })
    void form.trigger("callbackHeaders")
  }

  async function handleTestWebhook() {
    const url = form.getValues("callbackUrl")?.trim() ?? ""
    try {
      validateWebhookUrl(url)
    } catch {
      toast.error(t("campaigns.action.availableActions.SendWebhook.test.invalidUrl"))
      return
    }

    const template =
      form.getValues("callbackPayload")?.trim() || CAMPAIGN_API_CALLBACK_DEFAULT_PAYLOAD
    const response = await testWebhook({
      url,
      headers,
      payload: template,
      context: CAMPAIGN_API_CALLBACK_SAMPLE_DATA,
    })

    if (response.success) {
      toast.success(t("campaigns.action.availableActions.SendWebhook.test.success"))
    } else {
      toast.error(t("campaigns.action.availableActions.SendWebhook.test.error"))
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <FormField
        control={form.control}
        name="callbackUrl"
        render={({ field }: { field: ControllerRenderProps<FormValues, "callbackUrl"> }) => (
          <FormItem>
            <FormLabel>{t("campaigns.channel.api.url")}</FormLabel>
            <FormControl>
              <VaultVariableAutocomplete
                variables={CALLBACK_URL_VARIABLE_OPTIONS}
                className="w-full"
                disabled={!canEdit}
              >
                <Input
                  {...field}
                  value={field.value ?? ""}
                  onChange={(e) => {
                    form.setValue("callbackUrl", e.target.value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                    void form.trigger("callbackUrl")
                  }}
                  disabled={!canEdit}
                  placeholder={t(
                    "campaigns.action.availableActions.SendWebhook.config.urlPlaceholder",
                  )}
                  autoComplete="off"
                />
              </VaultVariableAutocomplete>
            </FormControl>
            <p className="text-sm text-muted-foreground">{t("campaigns.channel.api.urlHint")}</p>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="w-full flex items-end gap-2">
            <div className="flex flex-col gap-2 grow">
              <Label>{t("campaigns.action.availableActions.SendWebhook.config.headerKey")}</Label>
              <Input value="Content-Type" readOnly />
            </div>
            <div className="flex flex-col gap-2 grow">
              <Label>{t("campaigns.action.availableActions.SendWebhook.config.headerValue")}</Label>
              <Input value="application/json" readOnly />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon">
                  <IconInfoCircle className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("campaigns.action.availableActions.SendWebhook.config.defaultHeaderInfo")}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          {headers.map((header, index) => (
            <FormItem key={index} className="flex items-end gap-2">
              <div className="flex flex-col gap-2 grow">
                <Input
                  value={header.key}
                  onChange={(e) => {
                    const next = [...headers]
                    const row = next[index]
                    if (row) row.key = e.target.value
                    patchCallbackHeaders(next)
                  }}
                  disabled={!canEdit}
                  placeholder={t(
                    "campaigns.action.availableActions.SendWebhook.config.keyPlaceholder",
                  )}
                />
              </div>
              <div className="flex flex-col gap-2 grow">
                <Input
                  value={header.value}
                  onChange={(e) => {
                    const next = [...headers]
                    const row = next[index]
                    if (row) row.value = e.target.value
                    patchCallbackHeaders(next)
                  }}
                  disabled={!canEdit}
                  placeholder={t(
                    "campaigns.action.availableActions.SendWebhook.config.headerValuePlaceholder",
                  )}
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                type="button"
                disabled={!canEdit}
                onClick={() => {
                  const next = [...headers]
                  next.splice(index, 1)
                  patchCallbackHeaders(next)
                }}
              >
                <XIcon className="size-4" />
              </Button>
            </FormItem>
          ))}
          <Button
            variant="outline"
            size="sm"
            type="button"
            disabled={!canEdit}
            onClick={() => patchCallbackHeaders([...headers, { key: "", value: "" }])}
            className="w-fit"
          >
            <PlusIcon className="size-4" />
            {t("campaigns.action.availableActions.SendWebhook.config.addHeader")}
          </Button>
        </div>
      </div>

      <FormField
        control={form.control}
        name="callbackHeaders"
        render={() => (
          <FormItem>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex flex-col gap-2">
        <FormLabel>{t("campaigns.channel.api.requestBodyLabel")}</FormLabel>
        <p className="text-sm text-muted-foreground">
          {t("campaigns.channel.api.requestBodyDescription")}
        </p>
        <FormField
          control={form.control}
          name="callbackPayload"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-2">
              <MarkdownRenderer className="bg-muted rounded-md overflow-auto">
                {`\`\`\`json\n${field.value?.trim() || CAMPAIGN_API_CALLBACK_DEFAULT_PAYLOAD}\n\`\`\``}
              </MarkdownRenderer>
              <FormControl>
                <EditApiCallbackPayloadDialog
                  value={field.value ?? ""}
                  onChange={(payload) => {
                    form.setValue("callbackPayload", payload, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                    void form.trigger("callbackPayload")
                  }}
                  disabled={!canEdit}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          variant="testing"
          onClick={() => void handleTestWebhook()}
          disabled={isTestWebhookPending || !canEdit}
          className="ml-auto w-fit"
          type="button"
        >
          <FlaskConical className="size-4" />
          <span>{t("campaigns.action.availableActions.SendWebhook.test.title")}</span>
        </Button>
      </div>
    </div>
  )
}
